import { useState, type FormEvent, type ReactNode } from "react";
import { motion } from "motion/react";
import { Springy } from "./member/kit.tsx";
import { useNavigate } from "react-router";
import { api, useApi } from "../api.ts";
import { fmtDate, fmtRelative } from "../format.ts";
import { Badge, Button, Card, cn, CopyButton, EmptyState, ErrorText, Icon, Input, LoadError, Loading, Mono, useToast, type IconName } from "../ui/index.tsx";
import type { AnnouncementLevel, DetectionStatus, Game, GameStatus, Injection, TicketStatus } from "../../shared/types.ts";

type Tone = "neutral" | "ok" | "warn" | "bad" | "info" | "accent";

const GAME_STATUS: Record<GameStatus, { tone: Tone; label: string }> = {
  operational: { tone: "ok", label: "Operational" },
  development: { tone: "info", label: "In development" },
  maintenance: { tone: "warn", label: "Maintenance" },
  offline: { tone: "bad", label: "Offline" },
  discontinued: { tone: "neutral", label: "Discontinued" },
};

const DETECTION: Record<DetectionStatus, { tone: Tone; label: string }> = {
  undetected: { tone: "ok", label: "Undetected" },
  updating: { tone: "warn", label: "Updating" },
  detected: { tone: "bad", label: "Detected" },
  unknown: { tone: "neutral", label: "Unknown" },
};

export const GameStatusBadge = ({ status }: { status: GameStatus }) => (
  <Badge tone={GAME_STATUS[status]?.tone ?? "neutral"} dot>{GAME_STATUS[status]?.label ?? status}</Badge>
);

export const DetectionBadge = ({ status }: { status: DetectionStatus }) => (
  <Badge tone={DETECTION[status]?.tone ?? "neutral"}>{DETECTION[status]?.label ?? status}</Badge>
);

const INJECTION: Record<Injection, { tone: Tone; label: string; icon: IconName; title: string }> = {
  internal: { tone: "accent", label: "Internal", icon: "zap", title: "Runs inside the game process" },
  external: { tone: "info", label: "External", icon: "monitor", title: "Runs as a separate overlay process" },
  both: { tone: "neutral", label: "Internal + External", icon: "zap", title: "Both an internal and an external build" },
};

export const InjectionBadge = ({ injection }: { injection: Injection }) => {
  const m = INJECTION[injection];
  if (!m) return null;
  return <Badge tone={m.tone}><span title={m.title} className="inline-flex items-center gap-1.5"><Icon name={m.icon} className="size-3" />{m.label}</span></Badge>;
};

/** One chip per anti-cheat the title works with. Renders nothing for an empty list. */
export function AntiCheatBadges({ list }: { list: string[] }) {
  if (list.length === 0) return null;
  return (
    <>
      {list.map((name) => (
        <Badge key={name}><span title={`Works with games protected by ${name}`} className="inline-flex items-center gap-1.5"><Icon name="shield" className="size-3" />{name}</span></Badge>
      ))}
    </>
  );
}

/** Injection mode and anti-cheat chips, as one wrapping row. */
export const CompatBadges = ({ game, className }: { game: Pick<Game, "injection" | "anti_cheats">; className?: string }) => (
  <div className={`flex flex-wrap items-center gap-1.5 ${className ?? ""}`}>
    <InjectionBadge injection={game.injection} />
    <AntiCheatBadges list={game.anti_cheats} />
  </div>
);

/** Games that can't be bought sort after the ones that can; discontinued titles go last. */
export const saleRank = (g: Pick<Game, "status" | "purchasable">) => (g.purchasable ? 0 : g.status === "discontinued" ? 2 : 1);
export const bySaleRank = <T extends Pick<Game, "status" | "purchasable">>(a: T, b: T) => saleRank(a) - saleRank(b);

/** Email capture for a game that isn't on sale yet. Joins its launch waitlist. */
export function NotifyMe({ game, compact }: { game: Pick<Game, "id" | "name">; compact?: boolean }) {
  const [email, setEmail] = useState("");
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setPending(true);
    setError(null);
    try {
      await api.post(`/games/${encodeURIComponent(game.id)}/waitlist`, { email: email.trim() });
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  };

  if (done) {
    return (
      <div role="status" className="flex items-start gap-2 rounded-lg border border-ok/25 bg-ok/10 px-3 py-2.5 text-sm text-ok">
        <Icon name="check" className="mt-0.5 size-4 shrink-0" />
        <span>You're on the list — we'll email you when it launches.</span>
      </div>
    );
  }
  return (
    <form onSubmit={submit} className="space-y-2">
      <div className={compact ? "flex gap-2" : "flex flex-col gap-2 sm:flex-row"}>
        <Input type="email" required autoComplete="email" placeholder="you@example.com" aria-label={`Email for ${game.name} launch`} value={email}
          onChange={(e) => setEmail(e.target.value)} className={cn("min-w-0 flex-1", !compact && "h-11")} />
        <Button type="submit" variant="primary" size={compact ? "sm" : "lg"} icon="bell" loading={pending} className={compact ? "h-9" : undefined}>Notify me</Button>
      </div>
      <ErrorText>{error}</ErrorText>
    </form>
  );
}

/**
 * Stands in for buy buttons when a game can't be bought: a waitlist form while it's still coming,
 * a plain notice once discontinued. `compact` is for cards, the default for page sections.
 */
export function NotForSale({ game, compact }: { game: Pick<Game, "id" | "name" | "status">; compact?: boolean }) {
  if (game.status === "discontinued") {
    return (
      <div className={cn("flex items-start gap-2 rounded-lg border border-line bg-raised/60 text-muted", compact ? "px-3 py-2 text-xs" : "px-4 py-3 text-sm")}>
        <Icon name="info" className={cn("mt-0.5 shrink-0", compact ? "size-3.5" : "size-4")} />
        <span>No longer sold. Existing licenses keep working until they expire.</span>
      </div>
    );
  }
  const offline = game.status === "offline";
  return (
    <div className={compact ? "space-y-2" : "space-y-3"}>
      <div>
        <div className="kicker flex items-center gap-2 text-info"><span className="size-1.5 rounded-full bg-info" />{offline ? "Unavailable" : "Coming soon"}</div>
        {!compact && <p className="mt-1.5 text-sm text-muted">{offline ? "Sales are paused for now." : "Leave your email and we'll tell you the moment it launches."}</p>}
      </div>
      <NotifyMe game={game} compact={compact} />
    </div>
  );
}

const TICKET_TONE: Record<TicketStatus, Tone> = { open: "info", in_progress: "warn", resolved: "ok", closed: "neutral" };
const TICKET_LABEL: Record<TicketStatus, string> = { open: "Open", in_progress: "In progress", resolved: "Resolved", closed: "Closed" };

export const TicketStatusBadge = ({ status }: { status: TicketStatus }) => (
  <Badge tone={TICKET_TONE[status] ?? "neutral"} dot>{TICKET_LABEL[status] ?? status}</Badge>
);

export const LEVEL_TONE: Record<AnnouncementLevel, Tone> = { info: "info", warning: "warn", critical: "bad" };

export interface LoaderLatest {
  version: string | null;
  sha256?: string;
  changelog?: string | null;
  created_at?: string;
}

export function LoaderCard({ full }: { full?: boolean }) {
  const loader = useApi<LoaderLatest>("/loader/latest");
  const l = loader.data;
  return (
    <Card className="relative overflow-hidden">
      <div className="pointer-events-none absolute -right-16 -top-16 size-56 rounded-full bg-accent/10 blur-3xl" />
      {loader.loading && !l ? <Loading /> : loader.error && !l ? <LoadError error={loader.error} retry={loader.reload} /> : !l?.version ? (
        <div className="relative text-sm text-muted">No loader build has been published yet. Check back soon.</div>
      ) : (
        <div className="relative flex flex-col gap-5">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="relative grid size-11 shrink-0 place-items-center rounded-xl bg-accent-soft text-accent">
                <span className="absolute inset-0 animate-ping rounded-xl bg-accent/20 [animation-duration:2.8s] motion-reduce:hidden" aria-hidden />
                <motion.span className="relative" animate={{ y: [0, 2.5, 0] }} transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}>
                  <Icon name="download" className="size-5" />
                </motion.span>
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-base font-semibold">ExoWare loader</h2>
                  <Badge tone="accent">v{l.version}</Badge>
                </div>
                <p className="mt-0.5 text-xs text-muted">Released {fmtRelative(l.created_at)} ({fmtDate(l.created_at)}). Windows 10/11 x64.</p>
              </div>
            </div>
            <a href="/api/v2/loader/download"><Springy><Button variant="primary" size="lg" icon="download">Download loader</Button></Springy></a>
          </div>
          {l.sha256 && (
            <div className="flex items-center gap-2 rounded-lg border border-line bg-bg px-3 py-2">
              <span className="text-xs text-faint">SHA-256</span>
              <Mono className="min-w-0 flex-1 truncate">{l.sha256}</Mono>
              <CopyButton text={l.sha256} label="Copy SHA-256" />
            </div>
          )}
          {l.changelog && (
            <div>
              <div className="mb-1.5 text-xs font-medium text-muted">{full ? "Changelog" : "What's new"}</div>
              <Prose text={l.changelog} className={full ? undefined : "line-clamp-4"} />
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

/** Renders untrusted plain text as paragraphs; blank lines split paragraphs, single newlines are kept. */
export function Prose({ text, className }: { text: string; className?: string }) {
  const paras = text.replace(/\r\n/g, "\n").split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <div className={className}>
      {paras.map((p, i) => <p key={i} className="whitespace-pre-line text-sm leading-relaxed text-muted [&:not(:first-child)]:mt-3">{p}</p>)}
    </div>
  );
}

export function SectionTitle({ children, action }: { children: ReactNode; action?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <h2 className="text-sm font-semibold">{children}</h2>
      {action}
    </div>
  );
}

/** Games with their pricing tiers; picking a tier opens a purchase-request ticket. */
export function PurchaseOptions() {
  const games = useApi<Game[]>("/games");
  const navigate = useNavigate();
  const toast = useToast();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stripe = useStripeEnabled();

  const request = async (game: Game, tierIndex: number) => {
    const key = `${game.id}:${tierIndex}`;
    setBusy(key);
    setError(null);
    try {
      if (stripe) { await startCheckout(game.id, tierIndex); return; }
      const r = await api.post<{ ticket_id: number }>("/purchase-requests", { game_id: game.id, tier_index: tierIndex });
      toast("Purchase request sent", "success");
      navigate(`/support/${r.ticket_id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(null);
    }
  };

  const priced = (games.data ?? []).filter((g) => g.active !== false && g.purchasable && g.pricing_tiers.length > 0);
  if (games.loading && !games.data) return <Loading />;
  if (games.error && !games.data) return <LoadError error={games.error} retry={games.reload} />;
  if (priced.length === 0) return <EmptyState icon="card" title="Nothing for sale right now" body="Open a support ticket and we will help you out." />;
  return (
    <div className="space-y-5">
      <ErrorText>{error}</ErrorText>
      <div className="grid gap-4 md:grid-cols-2">
        {priced.map((g) => (
          <div key={g.id} className="rounded-lg border border-line bg-bg p-4">
            <div className="mb-3 flex items-center justify-between gap-2">
              <span className="font-medium">{g.name}</span>
              <DetectionBadge status={g.detection_status} />
            </div>
            <CompatBadges game={g} className="-mt-1 mb-3" />
            <ul className="space-y-2">
              {g.pricing_tiers.map((t, i) => (
                <li key={`${t.dur}-${i}`} className="flex items-center justify-between gap-3 text-sm">
                  <span className="text-muted">{t.dur}</span>
                  <span className="flex items-center gap-3">
                    <span className="font-semibold tabular-nums">{t.price}</span>
                    <Button size="sm" variant="secondary" loading={busy === `${g.id}:${i}`} disabled={busy !== null} onClick={() => request(g, i)}>{stripe ? "Buy" : "Request"}</Button>
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <p className="text-xs text-faint">{stripe
        ? "Pay by card through Stripe. Access is added to your account as soon as the payment clears."
        : "Requesting opens a support ticket. An admin confirms payment details and activates your license."}</p>
    </div>
  );
}

const ART_GRADIENTS: Record<string, string> = {
  survival: "from-emerald-500/40 via-teal-700/30 to-bg",
  fps: "from-accent/50 via-orange-700/30 to-bg",
  sandbox: "from-lime-500/40 via-green-800/30 to-bg",
  other: "from-sky-500/40 via-indigo-800/30 to-bg",
};

/** Cover image for a game, falling back to a generated gradient with the game's initials when no image is set or it fails to load. */
export function GameArt({ game, className }: { game: Pick<Game, "name" | "image_url" | "category">; className?: string }) {
  const [broken, setBroken] = useState(false);
  const initials = game.name.replace(/[^A-Za-z0-9 ]/g, " ").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase();
  return (
    <div className={`relative overflow-hidden bg-raised ${className ?? ""}`}>
      {game.image_url && !broken ? (
        <img src={game.image_url} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => setBroken(true)} className="size-full object-cover" />
      ) : (
        <div className={`grid size-full place-items-center bg-gradient-to-br ${ART_GRADIENTS[game.category] ?? ART_GRADIENTS.other}`}>
          <span className="text-4xl font-bold tracking-tighter text-fg/80 drop-shadow">{initials}</span>
        </div>
      )}
    </div>
  );
}

/** Whether card checkout is available; cached for the page's lifetime. */
export function useStripeEnabled(): boolean {
  const cfg = useApi<{ stripe_enabled: boolean }>("/billing/config");
  return !!cfg.data?.stripe_enabled;
}

/** Opens ExoWare's own checkout page for a tier. */
export async function startCheckout(gameId: string, tierIndex: number) {
  window.location.assign(`/checkout/${encodeURIComponent(gameId)}/${tierIndex}`);
}
